// What Me's pages show while Me lives somewhere else (MeAway in the app's
// extensions), and the word over them while Me moves. The host decides when;
// these only draw it, the same in the cloud's web and on a Hub.
import { Link } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import type { ReactNode } from "react";
import { buttonClass } from "../ui/button";
import { Icon, type IconKind } from "../ui/icon";

/** In place of Me's page: where Me is, and the way to the settings. */
export function MeAwayPanel({
  title,
  body,
  detail,
  settings,
}: {
  title: string;
  body: ReactNode;
  /** Shown under the body (what is away). */
  detail?: ReactNode;
  /** The settings page that moves Me (none: no way from here). */
  settings?: string;
}) {
  return (
    <div
      data-me-away=""
      className="flex min-h-0 flex-1 items-center justify-center bg-surface px-6"
    >
      <div className="max-w-[420px] text-center">
        <div className="text-[15px] text-fg">{title}</div>
        <div className="mt-2 text-[13px] leading-[1.7] text-muted">{body}</div>
        {detail && <div className="mt-3">{detail}</div>}
        {settings && (
          <div className="mt-4 flex justify-center">
            <Link to={settings} className={buttonClass()}>
              {m.shell_me_away_settings()}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

/** A small word at the bottom while Me moves (its pages read only). */
export function MeAwayNote({ text }: { text: string }) {
  return (
    <div
      role="status"
      data-me-away-note=""
      className="pointer-events-none fixed inset-x-0 bottom-3 z-[57] flex justify-center"
    >
      <div className="relative isolate inline-flex min-h-8 max-w-[calc(100vw-24px)] items-center rounded-2xl px-3 py-1 text-[13px] text-fg leading-[1.4]">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 rounded-[inherit] [transform:translateZ(0)]"
        >
          <span className="glass block size-full rounded-[inherit]" />
        </span>
        <span className="min-w-0 truncate">{text}</span>
      </div>
    </div>
  );
}

/** About how long is left, in words ("about 3 minutes left"). */
export function timeLeftText(etaSeconds: number | null | undefined): string {
  if (etaSeconds == null) return m.shell_me_moving_measuring();
  if (etaSeconds < 20) return m.shell_me_moving_almost();
  if (etaSeconds < 60) return m.shell_me_moving_under_minute();
  return m.shell_me_moving_minutes({ minutes: Math.round(etaSeconds / 60) });
}

/**
 * How far personal data has moved: a thin bar and "42% · about 1 minute
 * left" (the same on the Hub's settings and the cloud's screen).
 */
export function MeMoveProgress({
  percent,
  etaSeconds,
  align = "start",
}: {
  percent: number;
  etaSeconds: number | null | undefined;
  align?: "start" | "center";
}) {
  const p = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div
      data-me-move-progress=""
      className={`flex flex-col gap-1.5 ${align === "center" ? "items-center" : ""}`}
    >
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={p}
        aria-label={m.shell_me_moving_progress()}
        className="h-1 w-full max-w-[280px] overflow-hidden rounded-sm bg-hover"
      >
        <div
          className="h-1 bg-accent transition-[width] duration-700 ease-out"
          style={{ width: `${p}%` }}
        />
      </div>
      <div className="text-[12px] tabular-nums text-muted">
        {m.shell_me_moving_done({ percent: p, left: timeLeftText(etaSeconds) })}
      </div>
    </div>
  );
}

/** What personal data holds, with the sidebar's own icons and names. */
const PERSONAL_KINDS: readonly { icon: IconKind; name: () => string }[] = [
  { icon: "cases", name: () => m.cases_title() },
  { icon: "people", name: () => m.persons_title() },
  { icon: "documents", name: () => m.shell_personal_documents() },
  { icon: "agent", name: () => m.agents_nav() },
  { icon: "label", name: () => m.labels_title() },
  { icon: "favorite", name: () => m.shell_favorites() },
];

/**
 * The things that make up personal data (they move together), as the sidebar
 * shows them, and that the workspaces are not among them.
 */
export function PersonalDataKinds({
  small = false,
  align = "start",
}: {
  small?: boolean;
  align?: "start" | "center";
}) {
  return (
    <div data-personal-kinds="">
      <ul
        className={`flex flex-wrap gap-x-3 gap-y-1 ${
          align === "center" ? "justify-center" : ""
        } ${small ? "text-[12px]" : "text-[13px]"} text-body`}
      >
        {PERSONAL_KINDS.map((k) => (
          <li key={k.icon} className="inline-flex items-center gap-1">
            <Icon kind={k.icon} size={small ? 13 : 14} className="text-muted" />
            {k.name()}
          </li>
        ))}
      </ul>
      <p className={`mt-1 ${small ? "text-[11px]" : "text-[12px]"} text-muted`}>
        {m.shell_personal_data_not_workspace()}
      </p>
    </div>
  );
}
